import type { BillingEstimate } from '~/gql/billing/billing'
import { BillingPaymentTerm } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { formatCents } from '~/pages/backoffice/admin/billing/money'

type Props = { estimate?: BillingEstimate }

/**
 * Ce que le palier coûte sur le mois observé, et sur quoi le montant repose.
 *
 * Écran d'OBSERVATION : franchir une borne est signalé (préavis « X places avant
 * le palier suivant »), jamais appliqué — aucune session n'est jamais bloquée.
 */
function EstimateCards({ estimate }: Props) {
  const { t, i18n } = useTranslation()
  const money = (cents?: number | null) => (cents == null ? '—' : formatCents(cents, i18n.language))

  /** Montant dû ce mois : « sur devis » au-delà de XL, sinon le montant du palier. */
  const amount = () => {
    if (!estimate) return '—'
    if (estimate.isCustom) return t('billing.tier.CUSTOM')
    return money(estimate.monthlyAmountCents)
  }

  const amountHint = () => {
    if (!estimate || estimate.isCustom) return null
    return estimate.paymentTerm === BillingPaymentTerm.UPFRONT
      ? t('billing.amountDue.upfront', { amount: money(estimate.upfrontYearlyAmountCents) })
      : t('billing.amountDue.monthly')
  }

  /** « palier M — 32 places avant L », ou « sur devis » au-delà. */
  const tierHint = estimate
    ? estimate.isCustom
      ? t('billing.tier.CUSTOM.notice')
      : estimate.placesRemaining != null && estimate.nextTier
        ? t('billing.placesRemaining', {
            tier: t(`billing.tier.${estimate.billedTier}`),
            places: estimate.placesRemaining,
            next: t(`billing.tier.${estimate.nextTier}`),
          })
        : t(`billing.tier.${estimate.billedTier}`)
    : null

  const tiles = [
    { key: 'learnerCount', label: t('billing.learnerCount'), value: estimate?.learnerCount, hint: tierHint },
    { key: 'monthlyActive', label: t('billing.monthlyActive'), value: estimate?.monthlyActive },
    {
      key: 'leavingSoon',
      label: t('billing.leavingSoon'),
      value: estimate?.leavingWithinThreeMonths,
      hint: estimate ? t('billing.leavingSoon.help') : null,
    },
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('billing.amountDue')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <p className="text-3xl font-semibold tabular-nums">{amount()}</p>
          <p className="text-xs text-muted-foreground">{amountHint()}</p>
        </CardContent>
      </Card>

      {tiles.map(tile => (
        <Card key={tile.key}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {tile.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-3xl font-semibold tabular-nums">{tile.value ?? '—'}</p>
            {tile.hint && <p className="text-xs text-muted-foreground">{tile.hint}</p>}
          </CardContent>
        </Card>
      ))}

      {estimate && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground sm:col-span-2 lg:col-span-4">
          <Badge variant={estimate.isCustom ? 'outline' : 'default'}>
            {t('billing.billedTier', { tier: t(`billing.tier.${estimate.billedTier}`) })}
          </Badge>
          <span>{t('billing.neverBlocked')}</span>
        </div>
      )}
    </div>
  )
}

export default EstimateCards
